import { ReparoApi } from '@api';
import { DataSheet } from '@components/ui/DataSheet';
import { X } from '@phosphor-icons/react';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatCount } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export type PoType = 'INCOMING' | 'OUTGOING';

export type POStatus =
    | 'PENDING_REVIEW'
    | 'UNDER_PROCESSING'
    | 'DISPATCHED'
    | 'IN_PROGRESS'
    | 'PARTIALLY_PROCESSED'
    | 'CANCELLED'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'WAITING_FOR_APPROVAL'
    | 'OPEN'
    | 'REJECTED'
    | 'EXPIRED'
    | 'FULFILLED'
    | 'CREATED'
    | 'COMPLETED'
    | 'PENDING_ON_SUPPLIER'
    | 'PAYMENT_PENDING'
    | 'PAYMENT_DONE'
    | 'SHIPPED_TO_FF'
    | 'PENDING_DISPATCH_ON_FF'
    | 'SHIPPED'
    | 'SHIPPED_BY_FF'
    | 'RECEIVED_BY_FF'
    | 'INVOICE_DONE_BY_VENDOR'
    | 'SCHEDULED'
    | string;

export type ChannelId = string;

export interface PurchaseOrderRow {
    id: string;
    poNumber: string;
    channelId?: ChannelId;
    vendor?: string;
    totalQuantity: number;
    actualQuantity?: number;
    orderDate: string;
    expiryDate?: string;
    deliveryDate?: string;
    status: POStatus;
    side: 'demand' | 'supply';
    poRefNum?: string;
    excessQuantity?: number;
}

export interface PoItem {
    sku: string;
    name: string;
    orderedQty: number;
    dispatchedQty: number;
    /** Units received via excess-suffixed GRN bookings (subset of dispatchedQty). */
    excessQty?: number;
    /** Units the source system cancelled at line level (subset of orderedQty). */
    cancelledQty?: number;
    /** The reason the source stated for the cancelled units, when it gave one. */
    cancellationReason?: string | null;
}

export interface PoDetail {
    id: string;
    poNumber: string;
    status: POStatus;
    side: 'demand' | 'supply';
    channel?: ChannelId;
    vendor?: string;
    totalQuantity: number;
    orderDate: string;
    expiryDate?: string;
    grnIds?: string[];
    /** Subset of grnIds booked with an excess suffix. */
    excessGrnIds?: string[];
    items: PoItem[];
}

interface PurchaseOrderItemsListItemResponse {
    productId: string;
    sku?: string;
    name?: string;
    quantity: number;
    dispatchedQuantity: number;
    excessQuantity?: number;
    cancelledQuantity?: number;
    cancellationReason?: string | null;
}

interface PurchaseOrderItemsListResponse {
    purchaseOrderId: string;
    merchantId: string;
    channel?: string;
    vendor?: string;
    type: PoType;
    orderDate: string;
    expiryDate?: string;
    status: string;
    grnIds?: string[];
    excessGrnIds?: string[];
    items: PurchaseOrderItemsListItemResponse[];
}

/* -------------------------------------------------------------------------- */
/*                              Helper utilities                              */
/* -------------------------------------------------------------------------- */

export const formatDateDisplay = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });
};

export const normalizeStatus = (status?: string): POStatus => {
    if (!status) return 'PENDING_REVIEW';
    return status.trim().replace(/\s+/g, '_').toUpperCase() as POStatus;
};

const getStatusMeta = (
    status: POStatus,
): { label: string; className: string } => {
    const map: Record<POStatus, { label: string; className: string }> = {
        PENDING_REVIEW: {
            label: 'Pending Review',
            className: 'bg-orange-50 text-orange-600 border border-orange-100',
        },
        UNDER_PROCESSING: {
            label: 'Under Processing',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        DISPATCHED: {
            label: 'Dispatched',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        IN_PROGRESS: {
            label: 'In Progress',
            className: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
        },
        PARTIALLY_PROCESSED: {
            label: 'Partially Processed',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        CANCELLED: {
            label: 'Cancelled',
            className: 'bg-rose-50 text-rose-700 border border-rose-100',
        },
        PENDING_APPROVAL: {
            label: 'Pending Approval',
            className: 'bg-rose-50 text-rose-600 border border-rose-100',
        },
        WAITING_FOR_APPROVAL: {
            label: 'Pending Approval',
            className: 'bg-rose-50 text-rose-600 border border-rose-100',
        },
        APPROVED: {
            label: 'Approved',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        SCHEDULED: {
            label: 'Scheduled',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        EXPIRED: {
            label: 'Expired',
            className: 'bg-rose-50 text-rose-700 border border-rose-100',
        },
        OPEN: {
            label: 'Open',
            className: 'bg-slate-100 text-slate-700 border border-slate-200',
        },
        REJECTED: {
            label: 'Rejected',
            className: 'bg-rose-50 text-rose-700 border border-rose-100',
        },
        COMPLETED: {
            label: 'Completed',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        FULFILLED: {
            label: 'Fulfilled',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        PENDING_ON_SUPPLIER: {
            label: 'Pending on Supplier',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        PAYMENT_PENDING: {
            label: 'Payment Pending',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        PAYMENT_DONE: {
            label: 'Payment Done',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        SHIPPED_TO_FF: {
            label: 'Shipped to FF',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        PENDING_DISPATCH_ON_FF: {
            label: 'Pending Dispatch on FF',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        SHIPPED: {
            label: 'Shipped',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        SHIPPED_BY_FF: {
            label: 'Shipped by FF',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        RECEIVED_BY_FF: {
            label: 'Received by FF',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        INVOICE_DONE_BY_VENDOR: {
            label: 'Invoice Done by Vendor',
            className: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
        },
        CREATED: {
            label: 'Created',
            className: 'bg-slate-100 text-slate-700 border border-slate-200',
        },
        DRAFT: {
            label: 'Draft',
            className: 'bg-slate-100 text-slate-700 border border-slate-200',
        },
        ISSUED: {
            label: 'Issued',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        IN_PRODUCTION: {
            label: 'In Production',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        READY_TO_SHIP: {
            label: 'Ready to Ship',
            className: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
        },
        AT_DESTINATION: {
            label: 'At Destination',
            className: 'bg-sky-50 text-sky-700 border border-sky-100',
        },
        RECEIVED: {
            label: 'Received',
            className:
                'bg-emerald-50 text-emerald-700 border border-emerald-100',
        },
        PARTIALLY_RECEIVED: {
            label: 'Partially Received',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
        INVOICE_RAISED: {
            label: 'Invoice Raised',
            className: 'bg-amber-50 text-amber-700 border border-amber-100',
        },
    } as Record<POStatus, { label: string; className: string }>;

    if (map[status]) return map[status];
    const readable = status
        .toString()
        .toLowerCase()
        .split('_')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    return {
        label: readable,
        className: 'bg-slate-100 text-slate-700 border border-slate-200',
    };
};

export const StatusPill: React.FC<{ status: POStatus | string | null | undefined }> = ({ status }) => {
    // Accept either canonical UPPERCASE_UNDERSCORED keys or display strings
    // ("Approved", "At Destination") coming straight from the DB — normalize
    // before looking up so callers don't all need to remember.
    const normalized = normalizeStatus(status ?? undefined);
    const cfg = getStatusMeta(normalized);
    return (
        <span
            className={[
                'inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium',
                cfg.className,
            ].join(' ')}
        >
            {cfg.label}
        </span>
    );
};

export const MetaItem: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
            {label}
        </span>
        <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
);

/* -------------------------------------------------------------------------- */
/*                                   Drawer                                   */
/* -------------------------------------------------------------------------- */

interface PurchaseOrderDetailDrawerProps {
    poId: string | null;
    initialRow?: PurchaseOrderRow;
    side?: 'demand' | 'supply';
    channelLabelLookup?: Map<string, string>;
    onClose: () => void;
}

const PurchaseOrderDetailDrawer: React.FC<PurchaseOrderDetailDrawerProps> = ({
    poId,
    initialRow,
    side,
    channelLabelLookup,
    onClose,
}) => {
    const [detail, setDetail] = useState<PoDetail | null>(null);
    const [loading, setLoading] = useState(false);

    const { setShowAISidebar } = useAiAnalystContext((c) => c.actions);
    const { showAISideBar, aiChatMessages } = useAiAnalystContext(
        (c) => c.state,
    );

    useEffect(() => {
        if (!poId) {
            setDetail(null);
            return;
        }

        if (initialRow && initialRow.id === poId) {
            setDetail({
                id: initialRow.id,
                poNumber: initialRow.poNumber,
                status: initialRow.status,
                side: initialRow.side,
                channel: initialRow.channelId,
                vendor: initialRow.vendor,
                totalQuantity: initialRow.totalQuantity,
                orderDate: initialRow.orderDate,
                expiryDate: initialRow.expiryDate,
                items: [],
            });
        } else {
            setDetail({
                id: poId,
                poNumber: poId,
                status: 'PENDING_REVIEW',
                side: side ?? 'supply',
                totalQuantity: 0,
                orderDate: '',
                items: [],
            });
        }

        setLoading(true);
        let cancelled = false;
        const skeletonTotal = initialRow?.totalQuantity ?? 0;

        (async () => {
            try {
                const response = (await ReparoApi({
                    method: 'POST',
                    url: '/purchase-orders/items/list',
                    body: JSON.stringify({ purchaseOrderId: poId }),
                })) as unknown as PurchaseOrderItemsListResponse | null;

                if (cancelled) return;
                if (!response) {
                    setLoading(false);
                    return;
                }

                const itemsArray = Array.isArray(response.items)
                    ? response.items
                    : [];
                const parsedItems: PoItem[] = itemsArray.map(
                    (item): PoItem => ({
                        sku: item.sku || item.productId,
                        name: item.name || item.productId,
                        orderedQty: Number(item.quantity) || 0,
                        dispatchedQty: Number(item.dispatchedQuantity) || 0,
                        excessQty: Number(item.excessQuantity) || 0,
                        cancelledQty: Number(item.cancelledQuantity) || 0,
                        cancellationReason: item.cancellationReason ?? null,
                    }),
                );

                setDetail({
                    id: response.purchaseOrderId,
                    poNumber: response.purchaseOrderId,
                    status: normalizeStatus(response.status),
                    side: response.type === 'INCOMING' ? 'supply' : 'demand',
                    channel: response.channel,
                    vendor: response.vendor,
                    totalQuantity:
                        skeletonTotal ||
                        parsedItems.reduce(
                            (sum, item) => sum + item.orderedQty,
                            0,
                        ),
                    orderDate: formatDateDisplay(response.orderDate),
                    expiryDate: formatDateDisplay(response.expiryDate),
                    grnIds: response.grnIds || [],
                    excessGrnIds: response.excessGrnIds || [],
                    items: parsedItems,
                });
            } catch (error: any) {
                if (cancelled) return;
                const maybePayload = error ?? {};
                if (maybePayload && maybePayload.purchaseOrderId) {
                    const fallbackItems: PoItem[] = Array.isArray(
                        maybePayload.items,
                    )
                        ? maybePayload.items.map(
                              (item: any): PoItem => ({
                                  sku: item.sku || item.productId,
                                  name: item.name || item.productId,
                                  orderedQty: Number(item.quantity) || 0,
                                  dispatchedQty:
                                      Number(item.dispatchedQuantity) || 0,
                              }),
                          )
                        : [];

                    setDetail({
                        id: maybePayload.purchaseOrderId,
                        poNumber: maybePayload.purchaseOrderId,
                        status: normalizeStatus(maybePayload.status),
                        side:
                            maybePayload.type === 'INCOMING'
                                ? 'supply'
                                : 'demand',
                        channel: maybePayload.channel,
                        vendor: maybePayload.vendor,
                        totalQuantity:
                            fallbackItems.reduce(
                                (sum, item) => sum + item.orderedQty,
                                0,
                            ) || skeletonTotal,
                        orderDate: formatDateDisplay(maybePayload.orderDate),
                        expiryDate: formatDateDisplay(maybePayload.expiryDate),
                        grnIds: maybePayload.grnIds || [],
                        items: fallbackItems,
                    });
                }
                console.error('Failed to fetch PO detail', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [poId, initialRow, side]);

    const prevMessageCount = useRef(aiChatMessages.length);
    useEffect(() => {
        if (
            aiChatMessages.length > prevMessageCount.current &&
            !showAISideBar &&
            detail
        ) {
            setShowAISidebar(true);
        }
        prevMessageCount.current = aiChatMessages.length;
    }, [aiChatMessages.length, showAISideBar, detail, setShowAISidebar]);

    // Close AI sidebar whenever the drawer goes empty so it doesn't outlive the PO context
    useEffect(() => {
        if (!poId) {
            setShowAISidebar(false);
        }
    }, [poId, setShowAISidebar]);

    const handleClose = useCallback(() => {
        setShowAISidebar(false);
        onClose();
    }, [setShowAISidebar, onClose]);

    if (!poId || !detail) return null;

    const partyLabel =
        detail.side === 'demand'
            ? channelLabelLookup?.get(detail.channel || '') ||
              detail.channel ||
              '-'
            : detail.vendor || '-';

    return (
        <div className="fixed inset-0 z-30 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm">
            <div className="relative flex h-full w-full max-w-5xl flex-col gap-4 border-l border-slate-200 bg-white shadow-2xl">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-3">
                            <h3 className="text-lg font-semibold text-slate-900 sm:text-xl">
                                {detail.poNumber}
                            </h3>
                            <StatusPill status={detail.status} />
                        </div>
                        <p className="text-sm text-slate-500">{partyLabel}</p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Close PO details"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 pb-6">
                    {!showAISideBar && (
                        <>
                            <div className="grid gap-3 rounded-2xl bg-slate-50 p-4 text-sm text-slate-700 sm:grid-cols-4">
                                <MetaItem
                                    label={
                                        detail.side === 'demand'
                                            ? 'Channel'
                                            : 'Vendor'
                                    }
                                    value={partyLabel}
                                />
                                <MetaItem
                                    label="Total Quantity"
                                    value={formatCount(detail.totalQuantity)}
                                />
                                <MetaItem
                                    label="Order Date"
                                    value={detail.orderDate}
                                />
                                <MetaItem
                                    label={
                                        detail.side === 'demand'
                                            ? 'Expiry Date'
                                            : 'Expected Date'
                                    }
                                    value={detail.expiryDate || '-'}
                                />
                                {detail.grnIds && detail.grnIds.length > 0 ? (
                                    // Full-width row so long GRN lists wrap horizontally
                                    // instead of stacking inside one grid cell.
                                    <div className="flex flex-col gap-1 sm:col-span-4">
                                        <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                                            GRN Numbers
                                        </span>
                                        <div className="flex flex-wrap gap-1.5">
                                            {detail.grnIds.map((grnId) => {
                                                const isExcess =
                                                    detail.excessGrnIds?.includes(
                                                        grnId,
                                                    );
                                                return (
                                                    <span
                                                        key={grnId}
                                                        className={
                                                            isExcess
                                                                ? 'rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700'
                                                                : 'rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700'
                                                        }
                                                        title={
                                                            isExcess
                                                                ? 'Booked against an excess GRN'
                                                                : undefined
                                                        }
                                                    >
                                                        {grnId}
                                                        {isExcess ? ' · excess' : ''}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <DataSheet<PoItem>
                                data={detail.items}
                                columns={[
                                    {
                                        key: 'name',
                                        header: 'SKU Name',
                                        width: 200,
                                        render: (value) => (
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    color: 'rgb(15, 23, 42)',
                                                }}
                                            >
                                                {value}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'sku',
                                        header: 'SKU',
                                        width: 140,
                                        render: (value) => (
                                            <span
                                                style={{
                                                    color: 'rgb(71, 85, 105)',
                                                }}
                                            >
                                                {value}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'orderedQty',
                                        header: 'Ordered Quantity',
                                        align: 'right',
                                        width: 130,
                                        render: (value, row) => (
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontVariantNumeric:
                                                        'tabular-nums',
                                                }}
                                            >
                                                {row.orderedQty === 0 &&
                                                row.dispatchedQty > 0 ? (
                                                    <span className="mr-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                        Not ordered
                                                    </span>
                                                ) : null}
                                                {value != null ? formatCount(value) : '—'}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'dispatchedQty',
                                        header:
                                            detail.side === 'demand'
                                                ? 'Dispatched Quantity'
                                                : 'Received Quantity',
                                        align: 'right',
                                        width: 140,
                                        render: (value, row) => (
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontVariantNumeric:
                                                        'tabular-nums',
                                                }}
                                            >
                                                {value != null ? formatCount(value) : '—'}
                                                {(row.excessQty ?? 0) > 0 ? (
                                                    <span className="ml-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                        {formatCount(row.excessQty)}{' '}
                                                        excess
                                                    </span>
                                                ) : null}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'cancelledQty',
                                        header: 'Cancelled',
                                        align: 'right',
                                        width: 150,
                                        render: (value, row) =>
                                            (value ?? 0) > 0 ? (
                                                <span
                                                    title={row.cancellationReason ?? undefined}
                                                    className="rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700"
                                                >
                                                    {formatCount(value)}
                                                    {row.cancellationReason
                                                        ? ` · ${row.cancellationReason}`
                                                        : ''}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            ),
                                    },
                                ]}
                                title="Items"
                                isLoading={loading}
                                maxHeight={400}
                                getRowKey={(item) => item.sku}
                                getRowClassName={(item) =>
                                    (item.excessQty ?? 0) > 0
                                        ? 'bg-amber-50/60'
                                        : ''
                                }
                                emptyState="No items found for this PO."
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderDetailDrawer;
